import React, { useState } from "react";
import { X, Tag, Calendar, Clock, DollarSign, RefreshCw, Sparkles, Loader2 } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import "./CustomOfferModal.css";

export default function CustomOfferModal({ isOpen, onClose, onSubmit, conversation }) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [deliveryDays, setDeliveryDays] = useState(3);
  const [revisions, setRevisions] = useState(2);
  const [expirationDays, setExpirationDays] = useState(3);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError(isAr ? "يرجى كتابة عنوان العرض المخصص." : "Please enter custom offer title.");
      return;
    }
    if (!price || Number(price) <= 0) {
      setError(isAr ? "يرجى تحديد سعر العرض بشكل صحيح." : "Please enter a valid offer price.");
      return;
    }
    if (!deliveryDays || Number(deliveryDays) < 1) {
      setError(isAr ? "يرجى تحديد مدة التسليم (يوم واحد على الأقل)." : "Please set delivery days (min 1).");
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      let expiresAt = null;
      if (expirationDays > 0) {
        const exp = new Date();
        exp.setDate(exp.getDate() + Number(expirationDays));
        expiresAt = exp.toISOString().slice(0, 19).replace("T", " ");
      }

      const payload = {
        conversation_id: conversation.id,
        title: title.trim(),
        description: description.trim() || null,
        price: Number(price),
        delivery_days: Number(deliveryDays),
        revisions: Number(revisions),
        expires_at: expiresAt,
      };

      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || (isAr ? "تعذر إنشاء العرض المخصص." : "Failed to create custom offer."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="custom-offer-modal-overlay" dir={isAr ? "rtl" : "ltr"}>
      <div className="custom-offer-modal-card">
        <div className="custom-offer-modal-header">
          <div className="custom-offer-title-group">
            <div className="custom-offer-icon-badge">
              <Sparkles size={20} />
            </div>
            <div>
              <h3>{isAr ? "تقديم عرض مخصص (Custom Offer)" : "Create Custom Offer"}</h3>
              <p>{isAr ? "قم بتقديم عرض بسعر ونطاق عمل مخصص للعميل" : "Send a customized offer with special pricing to the client"}</p>
            </div>
          </div>
          <button type="button" className="custom-offer-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="custom-offer-form">
          {error && (
            <div className="custom-offer-error-alert">
              <span>{error}</span>
            </div>
          )}

          {/* Title */}
          <div className="custom-offer-form-group">
            <label>
              <Tag size={15} />
              <span>{isAr ? "عنوان العرض / الخدمة المخصصة *" : "Offer Title / Service Name *"}</span>
            </label>
            <input
              type="text"
              placeholder={isAr ? "مثال: تصميم شعار مخصص + هوية بصرية كاملة بخصم خاص" : "e.g. Logo Design & Full Branding Package"}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Description */}
          <div className="custom-offer-form-group">
            <label>
              <span>{isAr ? "تفاصيل ونطاق العمل المشمول" : "Scope of Work / Description"}</span>
            </label>
            <textarea
              rows={3}
              placeholder={isAr ? "اكتب بالتفصيل ما سيتم تسليمه والخصم المتفق عليه..." : "Describe the deliverables and agreed discount..."}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Price & Delivery Row */}
          <div className="custom-offer-grid-row">
            <div className="custom-offer-form-group">
              <label>
                <DollarSign size={15} />
                <span>{isAr ? "السعر المخصص (USD) *" : "Custom Price ($USD) *"}</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="1"
                placeholder="120"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </div>

            <div className="custom-offer-form-group">
              <label>
                <Calendar size={15} />
                <span>{isAr ? "مدة التسليم (أيام) *" : "Delivery Duration (Days) *"}</span>
              </label>
              <input
                type="number"
                min="1"
                value={deliveryDays}
                onChange={(e) => setDeliveryDays(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Revisions & Expiration Row */}
          <div className="custom-offer-grid-row">
            <div className="custom-offer-form-group">
              <label>
                <RefreshCw size={15} />
                <span>{isAr ? "عدد التعديلات المتاحة" : "Number of Revisions"}</span>
              </label>
              <input
                type="number"
                min="0"
                value={revisions}
                onChange={(e) => setRevisions(e.target.value)}
              />
            </div>

            <div className="custom-offer-form-group">
              <label>
                <Clock size={15} />
                <span>{isAr ? "انتهاء صلاحية العرض" : "Offer Expiration"}</span>
              </label>
              <select
                value={expirationDays}
                onChange={(e) => setExpirationDays(Number(e.target.value))}
              >
                <option value={1}>{isAr ? "ينتهي خلال 24 ساعة" : "Expires in 24 hours"}</option>
                <option value={3}>{isAr ? "ينتهي خلال 3 أيام" : "Expires in 3 days"}</option>
                <option value={7}>{isAr ? "ينتهي خلال 7 أيام" : "Expires in 7 days"}</option>
                <option value={0}>{isAr ? "بدون تاريخ انتهاء" : "No Expiration Date"}</option>
              </select>
            </div>
          </div>

          <div className="custom-offer-modal-actions">
            <button type="button" className="custom-offer-cancel-btn" onClick={onClose} disabled={submitting}>
              {isAr ? "إلغاء" : "Cancel"}
            </button>
            <button type="submit" className="custom-offer-submit-btn" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 size={16} className="custom-offer-spinner" />
                  <span>{isAr ? "جارِ إرسال العرض..." : "Sending Offer..."}</span>
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  <span>{isAr ? "إرسال العرض المخصص" : "Send Custom Offer"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
